<template>
    <form-select :id="`item-sku-${id}`"
                 :value="value"
                 name="sku"
                 v-bind="$attrs"
                 :options="skus"
                 :searchable="true"
                 :search-delay="1000"
                 :loading="skusLoading"
                 :internal-search="false"
                 :clear-on-select="false"
                 :preserve-search="true"
                 :options-limit="skuOptionsLimit"
                 placeholder=""
                 option-label="sku"
                 value-field="sku"
                 track-by="sku"
                 @input="onSkuChange"
                 @search="skusSearch"
                 @clear="skusClear"
    >
        <template slot="option"
                  slot-scope="props"
        >
            <div class="option__desc">
                <span class="option__title">
                    {{ props.option.sku + ' : ' + props.option.description }}
                </span>
            </div>
        </template>
        <template slot="afterList">
            <div v-if="skusHaveNextPage"
                 v-observe-visibility="skusReachedEndOfList"
                 class="skus-end-of-list"
            >
                &nbsp;
            </div>
        </template>
    </form-select>
</template>

<script>
// import axios from 'axios';
import FormSelect from '@/components/form/FormSelect';
import itemsApi from '@/services/resources/itemsApi';

export default {
    name: 'ItemsTypeahead',
    components: {
        FormSelect,
    },
    props: {
        id: {
            type: String,
            default: '',
        },
        item: {
            type: Object,
            default: () => ({}),
        },
        value: {
            type: null,
            default: undefined,
        },
        preloadedOptions: {
            type: Object,
            default: null,
        },
        useTemplates: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        const model = Object.assign({}, this.item);

        return {
            model,
            request: null,
            skus: [],
            skusLoading: false,
            currentSkuPage: 1,
            totalSkuPages: 1,
            skuSearchQuery: '',
            skuOptionsLimit: 1000,
        };
    },
    watch: {
        item(val) {
            this.model = Object.assign({}, val);
        },
    },
    async mounted() {
        await this.loadSettings();
    },
    methods: {
        async loadSettings() {
            this.skusLoading = true;

            if (!this.preloadedOptions || !this.preloadedOptions.items || !this.preloadedOptions.pageMetadata) {
                const { items, pageMetadata } = await this.$monitor(() => itemsApi.search());

                if (items) {
                    this.skus = items;
                }

                if (pageMetadata) {
                    this.totalSkuPages = pageMetadata.totalPages;
                    this.skuOptionsLimit = pageMetadata.totalResults + 1;
                }

                const preloadedOptions = {
                    items: this.skus,
                    pageMetadata,
                };

                this.$emit('skus-loaded', preloadedOptions);
            } else {
                this.skus = this.preloadedOptions.items;
                this.totalSkuPages = this.preloadedOptions.pageMetadata.totalPages;
                this.skuOptionsLimit = this.preloadedOptions.pageMetadata.totalResults + 1;
            }

            this.skusLoading = false;

            this.checkSelectedSkuOption();
        },
        checkSelectedSkuOption() {
            if (this.model.sku) {
                // Find if the fake code was added before
                let selectedSku = this.skus.find(x => x.sku === this.model.sku && x.isFake);

                if (selectedSku) {
                    // Remove the fake item to make it being added at the end of the list
                    const index = this.skus.indexOf(selectedSku);
                    this.skus.splice(index, 1);
                }

                // Try to find live code
                selectedSku = this.skus.find(x => x.sku === this.model.sku);

                if (!selectedSku) {
                    // The Sku selected for the item is not on the loaded skus list yet
                    // Add a fake item to display it as the selected option otherwise it will be empty
                    this.skus.push({
                        sku: this.model.sku,
                        description: this.model.description,
                        isFake: true,
                    });
                }
            }
        },
        async loadSkus(clearSkus = false) {
            this.skusLoading = true;

            const { items, pageMetadata } = await this.$monitor(() => itemsApi.search(this.currentSkuPage,
                this.skuSearchQuery));

            if (clearSkus) {
                this.skus = items;
            } else {
                this.skus = this.skus.concat(items);
            }

            if (pageMetadata) {
                this.totalSkuPages = pageMetadata.totalPages;
                this.skuOptionsLimit = pageMetadata.totalResults + 1;
            }

            const preloadedOptions = {
                items: this.skus,
                pageMetadata,
            };

            this.$emit('skus-loaded', preloadedOptions);

            this.checkSelectedSkuOption();

            this.skusLoading = false;
        },
        async skusSearch(searchQuery) {
            // Reset codes and page settings on search
            this.currentSkuPage = 1;
            this.totalSkuPages = 1;
            this.skuSearchQuery = searchQuery;

            await this.loadSkus(true);
        },
        async skusClear() {
            await this.skusSearch();
        },
        onSkuChange(option) {
            if (option === -1) {
                this.model.sku = null;
                this.model.description = null;
            } else {
                const selectedSku = this.skus.find(x => x.sku === option);

                if (selectedSku) {
                    this.model.sku = option;
                    this.model.description = selectedSku.description;

                    this.$emit('input', selectedSku);
                }
            }
        },
        skusHaveNextPage() {
            return this.currentSkuPage + 1 <= this.totalSkuPages;
        },
        async skusReachedEndOfList(reached) {
            if (reached && !this.skusLoading && this.skusHaveNextPage()) {
                this.currentSkuPage += 1;
                await this.loadSkus();
            }
        },
    },
};
</script>

<style scoped lang="scss">
.skus-end-of-list {
    width: 4px;
    height: 4px;
}
</style>
